import type { ProjectDetail } from '@fos/shared/contracts/projects';
import { PROJECT_BUDGET_WARNING_RATIO, PROJECT_STATUS_LABELS } from '@fos/shared/labels';
import { diffDaysLocal, todayLocal } from '@fos/shared/time';
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  Clock,
  FolderSearch,
  GitBranch,
  HandCoins,
  type LucideIcon,
  MoreHorizontal,
  Pencil,
  Receipt,
  SquareArrowOutUpRight,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDateShort, formatMoneyAuto, plural } from '../../../lib/format';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Markdown } from '../../../ui/Markdown';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Page } from '../../../ui/Page';
import { PageHeader, SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Stat } from '../../../ui/Stat';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { TabPanel, Tabs } from '../../../ui/Tabs';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON, TOUCH_ICON, TOUCH_TEXT } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { ProjectMembers } from '../components/ProjectMembers';
import { formatHours, isProjectOverdue, projectHoursText } from '../project-format';
import { getProjectOverviewSections, ProjectTabContent, type ProjectTabKey } from '../slots';

/**
 * Szczegóły projektu (/projekty/:id). Widok czeka na projekt w Figmie — zbudowany w duchu profilu klienta
 * (05 Profil REA): okruszki, tytuł, linia meta, akcje po prawej, zakładki Przegląd / Zadania / Czas / Wiedza /
 * Historia. Przegląd: kafelki (godziny, termin, rozliczanie), cel, zakres (Markdown), linki, zespół.
 * Pozostałe zakładki wypełniają inne moduły (`setProjectTab` w `../slots`).
 */
export function ProjectDetailPage() {
  const access = useAccess();
  if (!access.can('projects')) return <ModulePage module="projects" />;
  return <ProjectDetailView />;
}

const TABS: Array<{ value: 'overview' | ProjectTabKey; label: string }> = [
  { value: 'overview', label: 'Przegląd' },
  { value: 'tasks', label: 'Zadania' },
  { value: 'time', label: 'Czas' },
  { value: 'knowledge', label: 'Wiedza' },
  { value: 'history', label: 'Historia' },
];

function ProjectDetailView() {
  const { id = '' } = useParams();
  const query = trpc.projects.get.useQuery({ id }, { enabled: Boolean(id) });
  const [tab, setTab] = useState<string>('overview');
  const [editing, setEditing] = useState(false);
  const loaded = query.data;
  useRememberRecent(
    loaded
      ? {
          type: 'project',
          id: loaded.id,
          title: loaded.name,
          subtitle: loaded.client ? `projekt · ${loaded.client.name}` : 'projekt wewnętrzny',
          to: `/projekty/${loaded.id}`,
        }
      : null,
  );

  if (query.isPending) {
    return (
      <Page>
        <div role="status" aria-label="Wczytywanie projektu">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="mt-4 h-7 w-72" />
          <Skeleton className="mt-3 h-4 w-96 max-w-full" />
          <ListSkeleton rows={4} className="mt-10" />
        </div>
      </Page>
    );
  }
  if (query.isError) {
    const code = appErrorCode(query.error);
    return (
      <Page>
        <PageHeader breadcrumbs={[{ label: 'Projekty', to: '/projekty' }]} title="Projekt" />
        <Section className="mt-6">
          {code === 'NOT_FOUND' || code === 'VALIDATION_ERROR' ? (
            <EmptyState
              icon={FolderSearch}
              title="Nie ma takiego projektu"
              description="Projekt mógł zostać przeniesiony albo nie masz do niego dostępu."
              action={
                <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                  <Link to="/projekty">Wróć do projektów</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </Page>
    );
  }

  const project = query.data;
  return (
    <Page>
      <PageHeader
        detail
        breadcrumbs={[{ label: 'Projekty', to: '/projekty' }]}
        title={project.name}
        meta={<ProjectMeta project={project} />}
        actions={<ProjectActions project={project} onEdit={() => setEditing(true)} />}
      />
      {project.archivedAt && (
        <ContextBanner tone="info" icon={Archive} className="mt-5">
          Projekt jest w archiwum. Przywróć go, aby wprowadzać zmiany.
        </ContextBanner>
      )}
      <Tabs value={tab} onValueChange={setTab} items={TABS} aria-label="Sekcje projektu" className="mt-6">
        <TabPanel value="overview">
          <ProjectOverview project={project} />
        </TabPanel>
        {TABS.filter((t) => t.value !== 'overview').map((t) => (
          <TabPanel key={t.value} value={t.value}>
            <ProjectTabContent tab={t.value as ProjectTabKey} project={project} />
          </TabPanel>
        ))}
      </Tabs>
      <ProjectFormDialog open={editing} onOpenChange={setEditing} project={project} />
    </Page>
  );
}

function ProjectMeta({ project }: { project: ProjectDetail }) {
  const parts: ReactNode[] = [];
  if (project.client) {
    parts.push(
      <Link key="client" to={`/klienci/${project.client.id}`} className={`hover:text-fg hover:underline ${TOUCH_TEXT}`}>
        {project.client.name}
      </Link>,
    );
  } else if (project.isInternal) parts.push('Projekt wewnętrzny');
  parts.push(PROJECT_STATUS_LABELS[project.status]);
  if (project.lead) parts.push(`prowadzi ${project.lead.name}`);
  return (
    <span className="flex flex-wrap items-center gap-x-1.5">
      {parts.map((part, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stała kolejność części linii meta
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span aria-hidden>·</span>}
          {part}
        </span>
      ))}
    </span>
  );
}

function ProjectActions({ project, onEdit }: { project: ProjectDetail; onEdit: () => void }) {
  const utils = trpc.useUtils();
  const archive = trpc.projects.archive.useMutation();
  const restore = trpc.projects.restore.useMutation();
  if (!project.can.edit) return null;

  const refresh = () =>
    Promise.all([utils.projects.get.invalidate({ id: project.id }), utils.projects.list.invalidate()]);

  const doRestore = async (version?: number) => {
    try {
      await restore.mutateAsync({ id: project.id, version });
      await refresh();
      toast.success('Projekt przywrócony');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const doArchive = async () => {
    try {
      const archived = await archive.mutateAsync({ id: project.id, version: project.version });
      await refresh();
      toast.success('Projekt w archiwum', {
        description: 'Zadania i historia zostają.',
        action: { label: 'Cofnij', onClick: () => void doRestore(archived.version) },
      });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (project.archivedAt) {
    return (
      <Button
        variant="secondary"
        icon={ArchiveRestore}
        onClick={() => void doRestore()}
        loading={restore.isPending}
        className={TOUCH_BUTTON}
      >
        Przywróć
      </Button>
    );
  }
  return (
    <>
      <Button variant="secondary" icon={Pencil} onClick={onEdit} className={TOUCH_BUTTON}>
        Edytuj
      </Button>
      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" icon={MoreHorizontal} aria-label="Więcej akcji" className={TOUCH_ICON} />
        </MenuTrigger>
        <MenuContent align="end">
          <MenuItem icon={Archive} onSelect={() => void doArchive()}>
            Przenieś do archiwum
          </MenuItem>
        </MenuContent>
      </Menu>
    </>
  );
}

function dueNote(project: ProjectDetail): { text: string; tone: 'neutral' | 'warning' | 'danger' } | null {
  if (!project.endDate) return null;
  if (isProjectOverdue(project)) return { text: 'po terminie', tone: 'danger' };
  const days = diffDaysLocal(todayLocal(), project.endDate);
  if (days === 0) return { text: 'dziś', tone: 'warning' };
  if (days > 0 && days <= 7) return { text: `za ${days} ${plural(days, ['dzień', 'dni', 'dni'])}`, tone: 'warning' };
  if (days > 0) return { text: `za ${days} dni`, tone: 'neutral' };
  return null;
}

/** „Koszt partnerów” (owner / admin / manager): kwoty partnera wpisów projektu (DECISIONS 2026-10-08 [P]). */
function PartnerCostStat({ projectId }: { projectId: string }) {
  const usage = trpc.time.projectUsage.useQuery({ projectId });
  const cost = usage.data?.partnerCost;
  if (!cost) return null;
  return (
    <Stat
      label="Koszt partnerów"
      icon={HandCoins}
      value={formatMoneyAuto(cost.amount, cost.currency)}
      note={
        cost.unsettledAmount === cost.amount
          ? `${formatHours(cost.seconds)} h ze stawką partnera`
          : `do wypłaty ${formatMoneyAuto(cost.unsettledAmount, cost.currency)}`
      }
      showDot={false}
    />
  );
}

function ProjectOverview({ project }: { project: ProjectDetail }) {
  const access = useAccess();
  const budget = project.hoursBudgetSeconds;
  const due = dueNote(project);
  const hoursValue = projectHoursText(project.usedSeconds, budget) ?? '0 h';
  const left = budget ? budget - project.usedSeconds : null;
  const ratio = budget ? project.usedSeconds / budget : 0;
  const hoursTone = ratio > 1 ? 'danger' : ratio >= PROJECT_BUDGET_WARNING_RATIO ? 'warning' : 'neutral';

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
        <Stat
          label="Godziny"
          icon={Clock}
          value={hoursValue}
          progress={budget ? project.usedSeconds / budget : undefined}
          tone={hoursTone}
          showDot={hoursTone !== 'neutral'}
          note={
            budget
              ? left !== null && left >= 0
                ? `zostało ${formatHours(left)} h`
                : `ponad budżet o ${formatHours(-(left ?? 0))} h`
              : 'bez budżetu godzin'
          }
        />
        <Stat
          label="Termin"
          icon={CalendarClock}
          value={project.endDate ? formatDateShort(project.endDate) : '—'}
          note={due?.text ?? (project.startDate ? `od ${formatDateShort(project.startDate)}` : 'bez terminu')}
          tone={due?.tone ?? 'neutral'}
          showDot={Boolean(due && due.tone !== 'neutral')}
        />
        <Stat
          label="Rozliczanie"
          icon={Receipt}
          value={project.billableDefault ? 'Rozliczany' : 'Nierozliczany'}
          note="domyślnie dla wpisów czasu"
          showDot={false}
        />
        {project.can.partnerRates && access.can('time') && <PartnerCostStat projectId={project.id} />}
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-6">
        <div className="flex min-w-0 flex-col gap-4 md:gap-6">
          <Section>
            <SectionHeader tone="strong" title="Cel" />
            {project.goal ? (
              <p className="text-body-s whitespace-pre-line text-fg-secondary">{project.goal}</p>
            ) : (
              <p className="text-body-s text-fg-muted">Bez opisu celu.</p>
            )}
          </Section>
          <Section>
            <SectionHeader tone="strong" title="Zakres" />
            {project.scopeMd ? (
              <Markdown className="text-body-s">{project.scopeMd}</Markdown>
            ) : (
              <p className="text-body-s text-fg-muted">Zakres nie jest jeszcze opisany.</p>
            )}
          </Section>
          <Section>
            <SectionHeader tone="strong" title="Linki" />
            {project.repoUrl || project.figmaUrl ? (
              <ul>
                {project.repoUrl && <LinkRow icon={GitBranch} label="Repozytorium" url={project.repoUrl} />}
                {project.figmaUrl && <LinkRow icon={SquareArrowOutUpRight} label="Figma" url={project.figmaUrl} />}
              </ul>
            ) : (
              <p className="text-body-s text-fg-muted">Brak linków do repozytorium i Figmy.</p>
            )}
          </Section>
          {getProjectOverviewSections('left').map(({ key, component: Slot }) => (
            <Slot key={key} project={project} />
          ))}
        </div>
        <div className="flex min-w-0 flex-col gap-4 md:gap-6">
          {getProjectOverviewSections('right').map(({ key, component: Slot }) => (
            <Slot key={key} project={project} />
          ))}
          <ProjectMembers project={project} />
        </div>
      </div>
    </div>
  );
}

function LinkRow({ icon: Icon, label, url }: { icon: LucideIcon; label: string; url: string }) {
  let host = url;
  try {
    host = new URL(url).host + new URL(url).pathname.replace(/\/$/, '');
  } catch {
    // zostaw adres jak jest
  }
  return (
    <li className="flex min-h-11 items-center gap-3 border-b border-line py-2">
      <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      <span className="shrink-0 text-body-s text-fg-secondary">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 truncate text-body-s text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
      >
        {host}
      </a>
    </li>
  );
}
